import React from 'react';
import { Sparkles, FileUp, Pencil, Link2 } from 'lucide-react';

/**
 * CpscCreateLanding — CHA-267 PR-1 (BEHAVIOR-FROZEN). The "Create" landing: four method cards, one
 * per input method, each navigating to the (unchanged) existing component under its new home.
 * Upload & AI Decode is emphasized per the approved design. Cards ONLY navigate — nothing here
 * touches data, callables, or any compliance gate; the mounted components are byte-identical to
 * their old tab selves. Carbon tokens per CHA-239.
 */
const METHODS = [
  {
    view: 'create/decode', icon: Sparkles, emphasized: true,
    title: 'Upload & AI Decode',
    desc: 'Drop a lab report or exemption document — decode, review, and file. (AI optional; standard extraction always available.)',
  },
  {
    view: 'create/bv', icon: FileUp,
    title: 'BV Bulk Upload',
    desc: 'Upload a Bureau Veritas batch — many reports at once, then review under Drafts.',
  },
  {
    view: 'create/manual', icon: Pencil,
    title: 'Manual Entry',
    desc: 'Type a CoC directly, or upload the ChainTo template (non-BV labs).',
  },
  {
    view: 'create/link', icon: Link2,
    title: 'Link Existing',
    desc: 'Reference an already-issued certificate or exemption for a production style.',
  },
];

export default function CpscCreateLanding({ onSelect }) {
  // CHA-278 — Carbon pass (CPSC concept "Create: method picker"): square method cards with a 3px top bar
  // (accent on the emphasized card), icon tile, and an accent CTA line. Presentation only — the cards still
  // ONLY navigate via onSelect; exactly four buttons (pinned by CpscCreateManageNav.test).
  return (
    <div className="font-plex text-carbon-ink space-y-4">
      <p className="text-sm text-carbon-gray-70 max-w-xl leading-relaxed">How do you want to create it? All four methods end in the same operator review — nothing is filed without your confirmation.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" aria-label="cpsc create methods">
        {METHODS.map(({ view, icon: Icon, title, desc, emphasized }) => (
          <button
            key={view}
            onClick={() => onSelect(view)}
            aria-label={`create method ${title.toLowerCase()}`}
            className={`relative text-left bg-carbon-white border p-5 flex flex-col gap-2.5 transition-colors hover:border-carbon-blue-60 ${
              emphasized ? 'border-carbon-blue-60 border-t-[3px] border-t-carbon-blue-60' : 'border-carbon-border border-t-[3px] border-t-carbon-border'
            }`}
          >
            {emphasized && (
              <span className="absolute top-3 right-3 font-plex-mono text-[9px] tracking-[0.1em] uppercase bg-carbon-blue-60 text-white px-2 py-0.5">Recommended</span>
            )}
            <div className={`w-9 h-9 flex items-center justify-center ${emphasized ? 'bg-carbon-blue-60 text-white' : 'bg-carbon-gray-10 text-carbon-gray-70'}`}>
              <Icon className="h-4 w-4" />
            </div>
            <div className="text-[15px] font-semibold text-carbon-ink">{title}</div>
            <p className="text-xs text-carbon-gray-70 leading-relaxed">{desc}</p>
            <span className="mt-auto text-xs font-semibold text-carbon-blue-60">Open →</span>
          </button>
        ))}
      </div>
    </div>
  );
}
